import { localeFromPathname } from './locale-routing.js';
import type { Locale } from '@kilat/shared';

/**
 * Soft locale-suggestion decision (follow-up to PR #72 / ADR-0024).
 *
 * PR #72 correctly removed the first-launch `navigator.language` AUTO-redirect —
 * silently bouncing the bare canonical URL to `/ms` breaks canonicalisation and
 * is an SEO anti-pattern. But the standing product intent
 * ([[feedback-locale-detection-and-switcher]]) is that a first-touch visitor
 * shouldn't have to hunt for the language switcher. This module restores that
 * intent SEO-safely: it decides whether to OFFER (never force) a locale, and the
 * banner does a client-side navigation only on an explicit tap.
 *
 * These are pure string functions (no DOM, no router, no storage) so the
 * "should we suggest?" logic is unit-testable in isolation; the component reads
 * `navigator.languages` + localStorage and feeds the values in.
 */

/**
 * Map one BCP-47 language tag to a supported locale, or null. Mirrors the #70
 * brand-gate detection (`detectGateLocale`): only the primary subtag matters,
 * and Indonesian (`id`) folds to Malay (`ms`) since the two are mutually
 * intelligible and there's no `id` catalog.
 */
export function localeFromLanguageTag(tag: string, supported: readonly Locale[]): Locale | null {
  const head = tag.toLowerCase().slice(0, 2);
  const normalized = head === 'id' ? 'ms' : head;
  return (supported as readonly string[]).includes(normalized) ? (normalized as Locale) : null;
}

/**
 * The visitor's most-preferred SUPPORTED locale, taken from their ordered
 * `navigator.languages` list (index 0 wins), or null if none of their languages
 * maps to something we support.
 */
export function detectPreferredLocale(
  languages: readonly string[],
  supported: readonly Locale[],
): Locale | null {
  for (const tag of languages) {
    const match = localeFromLanguageTag(tag, supported);
    if (match) return match;
  }
  return null;
}

export interface SuggestLocaleInput {
  /** The current URL pathname (e.g. `/`, `/pricing`, `/ms/pricing`). */
  readonly pathname: string;
  /** Ordered device languages, most-preferred first (`navigator.languages`). */
  readonly languages: readonly string[];
  readonly supported: readonly Locale[];
  readonly defaultLocale: Locale;
  /** Whether the visitor already dismissed or acted on the banner. */
  readonly dismissed: boolean;
}

/**
 * Decide whether — and to which locale — to surface the suggestion banner.
 * Returns the target locale, or null for "don't show".
 *
 * Shows only when ALL hold:
 *  a) the URL is the bare/default-locale path — a `/ms|/zh|/ta` prefix means the
 *     visitor already chose a language, so we never second-guess it;
 *  b) the device's most-preferred supported language is a NON-default locale;
 *  c) the visitor hasn't already dismissed/acted on the banner.
 *
 * Never returns the default locale and never navigates — it's a pure decision;
 * the caller renders + (only on tap) navigates.
 */
export function suggestedLocale(input: SuggestLocaleInput): Locale | null {
  const { pathname, languages, supported, defaultLocale, dismissed } = input;
  if (dismissed) return null;
  // (a) only on the bare/default-locale path.
  if (localeFromPathname(pathname, supported, defaultLocale) !== defaultLocale) return null;
  // (b) device prefers a supported, non-default locale.
  const detected = detectPreferredLocale(languages, supported);
  if (!detected || detected === defaultLocale) return null;
  return detected;
}
